<template>
  <div>
    <div class="h-[50vh] min-h-20 max-h-[24rem] flex flex-col items-center justify-center" v-if="status === 'pending'">
      <AtomSpinner class="w-6" />
    </div>
    <div v-if="!curatedMeals?.length && status !== 'pending'">
      <AtomInfoBox
        :heading="$t('shared.noData.events.heading')"
        :description="noMealsDescription"
      />
    </div>
    <div class="grid gap-8" v-if="curatedMeals?.length && status !== 'pending' && !route.query?.debug">
      <div class="grid gap-8" v-if="curatedMeals?.length">
        <div v-for="meals, date in groupedMealsDate" :key="date" class="grid gap-2">
          <EventsListDate class="-mx-4 px-4" :date="date" />
          <div class="grid grid-cols-12 gap-4 md:gap-6">
            <div v-for="meal in meals" :key="meal.id" class="h-full col-span-full" :class="{ 'sm:col-span-6': (meals.length === 2 || meals.length === 4), 'sm:col-span-6 lg:col-span-4': (meals.length === 3 || meals.length > 4) }">
              <EventsListCard
                :date="date"
                :meal="meal"
                :restaurant="meal.restaurant"
                :style="meals.length === 1 ? 'promoted' : ((meals.length === 2 || meals.length === 4) ? 'featured' : '')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Meals for debugging -->
    <div class="grid gap-4" v-if="route.query?.debug">
      <div class="grid gap-4">
        <div class="font-bold">Curated Events</div>
        <table class="debug-table">
          <tbody>
            <tr>
              <th class="w-12">ID</th>
              <th class="w-12">Date</th>
              <th class="w-1/4">Restaurant</th>
              <th class="w-12">Rank</th>
              <th class="w-12">Promoted</th>
              <th class="w-12">Meal ID</th>
              <th class="w-1/4">Name</th>
              <th class="w-1/4">Desc</th>
              <th class="w-12">Type</th>
              <th class="w-12">AltType</th>
            </tr>
            <tr v-for="meal in curatedMeals" :key="meal.id">
              <td v-html="meal.restaurant?.id" />
              <td class="whitespace-nowrap" v-html="toDayjs().isAfter(meal.startDate) ? toDayjs().format('YYYY-MM-DD') : meal.startDate" />
              <td v-html="meal.restaurant?.name" />
              <td v-html="meal.restaurant?.rank" />
              <td>
                <div class="whitespace-nowrap" v-if="meal.restaurant?.promoted && meal.restaurant?.promoted?.promotionTypes?.includes('events')">
                  {{ meal.restaurant?.promoted?.text }} ({{ meal.restaurant?.promoted?.promotionTypes.join(',') }}, {{ meal.restaurant?.promoted?.rank }})
                  <br>
                  {{ meal.restaurant?.promoted?.startDate }} {{ meal.restaurant?.promoted?.endDate }}
                </div>
              </td>
              <td v-html="meal.id" />
              <td v-html="meal.name" />
              <td v-html="meal.description?.length > 10 ? meal.description.slice(0, 8) + '...' : meal.description" />
              <td v-html="meal.type" />
              <td v-html="meal.altType" />
            </tr>
          </tbody>
        </table>
      </div>
      <div class="grid gap-4">
        <div class="font-bold">All Events</div>
        <table class="debug-table">
          <tbody>
            <tr>
              <th class="w-12">ID</th>
              <th class="w-12">Date</th>
              <th class="w-1/4">Restaurant</th>
              <th class="w-12">Rank</th>
              <th class="w-12">Promoted</th>
              <th class="w-12">Meal ID</th>
              <th class="w-1/4">Name</th>
              <th class="w-1/4">Desc</th>
              <th class="w-12">Type</th>
              <th class="w-12">AltType</th>
            </tr>
            <tr v-for="meal in mealsData?.data" :key="meal.id">
              <td v-html="meal.restaurant?.id" />
              <td class="whitespace-nowrap" v-html="toDayjs().isAfter(meal.startDate) ? toDayjs().format('YYYY-MM-DD') : meal.startDate" />
              <td v-html="meal.restaurant?.name" />
              <td v-html="meal.restaurant?.rank" />
              <td>
                <div class="whitespace-nowrap" v-if="meal.restaurant?.promoted && meal.restaurant?.promoted?.promotionTypes?.includes('events')">
                  {{ meal.restaurant?.promoted?.text }} ({{ meal.restaurant?.promoted?.promotionTypes.join(',') }}, {{ meal.restaurant?.promoted?.rank }})
                  <br>
                  {{ meal.restaurant?.promoted?.startDate }} {{ meal.restaurant?.promoted?.endDate }}
                </div>
              </td>
              <td v-html="meal.id" />
              <td v-html="meal.name" />
              <td v-html="meal.description?.length > 10 ? meal.description.slice(0, 8) + '...' : meal.description" />
              <td v-html="meal.type" />
              <td v-html="meal.altType" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script async setup>
const { t } = useI18n() 
const route = useRoute()
const dayjs = useDayjs()
const app = useAppStore()
const fat = useFatStore()
const limitMealsStartPage = 36

// Get specific date
const isStartPage = computed(() => !route.query?.date && !route.query?.startDate && !route.query?.endDate)

// Get specific date
const date = computed(() => route.query?.date)
const startDate = computed(() => route.query?.startDate || dayjs().format('YYYY-MM-DD'))
const endDate = computed(() => route.query?.endDate || dayjs().add(31, 'days').format('YYYY-MM-DD'))

// Get City and City ID's from app store
const city = computed(() => app.cities?.find(city => city.slug === route.params?.city))
const cityIds = computed(() => [city.value?.id, ...city.value?.relatedCityIds || []])

// Fetch events
const { data: mealsData, status } = await useFetch('/events', {
  key: `events-${city.value?.id}`,
  // key: `events-${fat.cityId}`,
  baseURL: useEnv('apiBase'),
  query: {
    scope: 'bokabord.se',
    bookable: true,
    mealTypes: fat.defaultMealTypes.join(','),
    limit: computed(() => isStartPage.value ? 120 : 500),
    // cities: fat.cityIds.join(','),
    cities: computed(() => city.value ? cityIds.value.join(',') : undefined),
    startDate: computed(() => date.value || startDate?.value),
    endDate: computed(() => date.value || endDate?.value),
    maxDays: 8,
  }
})

// Filter meals without name
const meals = computed(() => (status.value !== 'pending'
  ? mealsData?.value?.data.filter(meal => meal.name.length > 2)
  : []))

// Sort meals by rank
const sortedMeals = computed(() => useOrderMealsByRank(meals.value))

// Set max meals by restaurant based on time interval
const maxMealsByRestaurant = computed(() => date.value ? 2 : 5)

// Filter restaurant occurrences based on max meals by restaurant 
const curatedMeals = computed(() => 
  useFilterMealsByMaxRestaurantOccurrences(sortedMeals.value, maxMealsByRestaurant.value))

// Get meals based on date caps, for events start page
const cappedMeals = computed(() => (isStartPage.value
  ? useFilterMealsForEventsStart(curatedMeals.value, limitMealsStartPage)
  : curatedMeals.value))

// Group meals by date
const groupedMealsDate = computed(() => useGroupMealsByDate(cappedMeals.value, date.value))

// Create message if no meals are found
const noMealsDescription = computed(() => isStartPage.value
  ? t('shared.noData.events.description', { city: fat.city?.name })
  : (date.value
    ? t('shared.noData.events.descriptionDate', { city: fat.city?.name, date: dayjs(date.value).format('D MMMM YYYY') })
    : t('shared.noData.events.descriptionPeriod', { city: fat.city?.name, startDate: dayjs(startDate.value).format('D MMM'), endDate: dayjs(endDate.value).format('D MMM YYYY') })))
</script>